<template>
  <svg
    viewBox="0 0 1000 60"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMinYMin meet"
    class="svg-content"
    v-if="days"
  >
    <desc>Last {{ count }} days uptime</desc>
    <rect
      v-for="(item, index) in days"
      v-bind:key="item.time"
      :x="`${index * elementSpace}cm`"
      :y="elementOffset(item)"
      :width="`${elementWidth}cm`"
      :height="elementHeight(item)"
      class="light"
      :class="statusClass(item)"
      v-tooltip="info(item)"
    />
  </svg>
</template>

<script>
// @link https://date-fns.org/v2.21.1/docs/parseISO
import { format, parseISO } from "date-fns";

const ELEMENT_WIDTH = 0.193; // cm
const ELEMENT_PADDING = 0.1; // cm
const ELEMENT_HEIGHT = 1.5; // cm
// by-state fractions
const ELEMENT_UP = 1;
const ELEMENT_WARNING = 0.8;
const ELEMENT_DOWN = 0.6;
const ELEMENT_NA = 0.4;

export default {
  props: {
    days: {
      type: Array,
      required: true,
    },
  },
  methods: {
    statusClass(item) {
      if (item.availability === null || item.downtime === null) {
        return "disabled";
      }
      if (item.downtime === 0) {
        return "operational";
      }
      if (item.downtime / 60 <= 10) {
        return "warning";
      }
      return "outage";
    },
    info(item) {
      const day = format(parseISO(item.time), "MMM do");
      if (item.availability === null || item.downtime === null) {
        return `${day}: No data recorded`;
      }
      if (item.availability === 100) {
        return `${day}: ${item.availability}% uptime`;
      }
      return `${day}: ${item.availability}% uptime, ${Math.round(
        item.downtime / 60
      )} mins downtime`;
    },
    elementHeight(item) {
      if (item.availability === null || item.downtime === null) {
        return `${this.elHeight * this.elNa}cm`; // no data
      }
      if (item.downtime === 0) {
        return `${this.elHeight * this.elUp}cm`; // up
      }
      if (item.downtime / 60 <= 10) {
        return `${this.elHeight * this.elWarning}cm`; // warn
      }
      return `${this.elHeight * this.elDown}cm`; // down
    },
    elementOffset(item) {
      if (item.availability === null || item.downtime === null) {
        return `${(this.elHeight * (1 - this.elNa)) / 2}cm`; // no data
      }
      if (item.downtime === 0) {
        return `${(this.elHeight * (1 - this.elUp)) / 2}cm`; // up
      }
      if (item.downtime / 60 <= 10) {
        return `${(this.elHeight * (1 - this.elWarning)) / 2}cm`; // warn
      }
      return `${(this.elHeight * (1 - this.elDown)) / 2}cm`; // down
    },
  },
  computed: {
    count() {
      return this.days.length;
    },
    elementWidth() {
      return ELEMENT_WIDTH;
    },
    elementPadding() {
      return ELEMENT_PADDING;
    },
    elementSpace() {
      return this.elementWidth + this.elementPadding;
    },
    elHeight() {
      return ELEMENT_HEIGHT;
    },
    elUp() {
      return ELEMENT_UP;
    },
    elWarning() {
      return ELEMENT_WARNING;
    },
    elDown() {
      return ELEMENT_DOWN;
    },
    elNa() {
      return ELEMENT_NA;
    },
  },
};
</script>

<style lang="scss">
.light {
  &.operational {
    fill: var(--accentUp);
  }
  &.warning {
    fill: var(--accentWarning);
  }
  &.outage {
    fill: var(--colorDown);
  }
  &.disabled {
    fill: var(--accentDisabled);
  }
}

.svg-content {
  display: inline-block;
  position: absolute;
  top: 0;
  left: 0;
}
</style>
